'use client'

import Link from 'next/link'
import { useRef, useEffect, useState } from 'react'

interface Month {
  label: string
  value: string // YYYY-MM
  slug: string  // e.g. april-2026
}

interface NewArrivalsHeroBannerProps {
  months: Month[]
  selectedMonth: string | null
}

export const NewArrivalsHeroBanner = ({
  months,
  selectedMonth,
}: NewArrivalsHeroBannerProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState)
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [])

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' })
  }

  return (
    <div className="mb-6 flex items-center gap-2 min-w-0">
      {canScrollLeft && <button
        onClick={() => scroll('left')}
        aria-label="Scroll left"
        className="flex-shrink-0 cursor-pointer rounded-full border border-gray-300 p-1.5 text-gray-500 transition-colors hover:border-gray-500 hover:text-gray-900"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
        </svg>
      </button>}

      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] min-w-0"
      >
        <Link
          href="/magic-tricks/"
          className={`flex-shrink-0 inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
            !selectedMonth
              ? 'border-gray-900 bg-gray-900 text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
          }`}
        >
          All New Arrivals
        </Link>
        {months.map(month => (
          <Link
            key={month.value}
            href={`/magic-tricks/${month.slug}/`}
            className={`flex-shrink-0 inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              selectedMonth === month.value
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
            }`}
          >
            {month.label}
          </Link>
        ))}
      </div>

      <button
        onClick={() => scroll('right')}
        disabled={!canScrollRight}
        aria-label="Scroll right"
        className="flex-shrink-0 cursor-pointer rounded-full border border-gray-300 p-1.5 text-gray-500 transition-colors hover:border-gray-500 hover:text-gray-900 disabled:opacity-20 disabled:pointer-events-none"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  )
}
